/**
 * Toast — ephemeral glass notification pills.
 *
 * Imperative API lives in `toastBus.ts`; this file only renders the React
 * container and subscribes it to that bus.
 * Toasts stack from the bottom-right with auto-dismiss (default 4 s).
 *
 * Pass `action` to surface a small inline CTA button (e.g. "Open Settings").
 * Clicking it runs the callback and dismisses the toast — the standard
 * inline-CTA UX. The accent color matches the toast severity so the action
 * inherits the toast's visual weight.
 */

import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, IconButton } from "./Button.tsx";
import { GLASS_TOOLBAR } from "./styles.ts";
import { registerToastSink, type ToastEntry } from "./toastBus.ts";
import {
  COLOR_ACCENT,
  COLOR_ERROR,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_WARNING,
  FONT_SIZE_SMALL,
  RADIUS_DOT,
  RADIUS_LARGE,
  Z_OVERLAY,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Severity colors (match ViewportMessages pattern)
// ---------------------------------------------------------------------------

const SEVERITY_ACCENT: Record<ToastEntry["severity"], string> = {
  info: COLOR_ACCENT,
  warning: COLOR_WARNING,
  error: COLOR_ERROR,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  // Register the imperative add function
  useEffect(() => {
    return registerToastSink((entry) =>
      setToasts((prev) => {
        if (entry.dedupKey && prev.some((t) => t.dedupKey === entry.dedupKey)) {
          return prev; // already visible, suppress duplicate
        }
        return [...prev, entry];
      }),
    );
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 8,
        zIndex: Z_OVERLAY,
        pointerEvents: "none",
      }}
    >
      {toasts.map((t) => (
        <ToastPill key={t.id} entry={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}

function ToastPill({
  entry,
  onDismiss,
}: {
  entry: ToastEntry;
  onDismiss: (id: number) => void;
}) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Animate in
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const dismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
    setTimeout(() => onDismiss(entry.id), 200);
  }, [entry.id, onDismiss]);

  // Auto-dismiss
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onDismiss(entry.id), 200);
    }, entry.duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [entry, onDismiss]);

  const accent = SEVERITY_ACCENT[entry.severity];

  const handleAction = useCallback(() => {
    // Run the user's callback first; if it throws we still want to dismiss
    // the toast so the UI doesn't strand the user with a stuck pill.
    try {
      entry.action?.onClick();
    } finally {
      dismiss();
    }
  }, [entry.action, dismiss]);

  return (
    <div
      style={{
        ...GLASS_TOOLBAR,
        borderRadius: RADIUS_LARGE,
        borderColor: `${accent}33`,
        padding: "10px 14px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        maxWidth: 360,
        pointerEvents: "auto",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.2s ease, transform 0.2s ease",
      }}
    >
      {/* Accent dot */}
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: RADIUS_DOT,
          background: accent,
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontSize: FONT_SIZE_SMALL,
          color: entry.severity === "error" ? accent : COLOR_TEXT,
          lineHeight: 1.4,
          flex: 1,
        }}
      >
        {entry.message}
      </span>
      {entry.action && (
        <Button
          onClick={handleAction}
          variant="ghost"
          size="xs"
          style={{
            color: accent,
            borderColor: `${accent}55`,
            borderStyle: "solid",
            borderWidth: 0.5,
            background: "transparent",
            flexShrink: 0,
          }}
        >
          {entry.action.label}
        </Button>
      )}
      <IconButton
        onClick={dismiss}
        icon={<X size={12} />}
        label="Dismiss notification"
        variant="ghost"
        size="xs"
        style={{
          width: 18,
          height: 18,
          padding: 0,
          color: COLOR_TEXT_DIM,
          flexShrink: 0,
        }}
      />
    </div>
  );
}
